// -----------------------------------------------------------------------------
// Auto-classification presentation helpers — band labels/colours and the
// human copy for classification-driven ReviewRequired reasons.
//
// Pure helpers only (no React/DOM) so StatusBanner and the classification badge
// share one source of truth for the band vocabulary and colours.
// -----------------------------------------------------------------------------

export interface BandStyle {
  /** Spanish label for the band (alta / media / baja). */
  label: string;
  /** Tailwind chip utility group (bg/text/border). */
  chip: string;
}

const BAND_STYLES: Record<string, BandStyle> = {
  high: { label: 'alta', chip: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  medium: { label: 'media', chip: 'bg-amber-50 text-amber-700 border border-amber-200' },
  low: { label: 'baja', chip: 'bg-rose-50 text-rose-700 border border-rose-200' },
};

/**
 * Band → label + chip classes. Falls back to a neutral slate chip for an unknown/legacy band so the
 * UI never renders an empty or mis-coloured pill.
 */
export function bandStyle(band: string | null | undefined): BandStyle {
  const key = band?.toLowerCase() ?? '';
  return BAND_STYLES[key] ?? { label: band ?? 'desconocida', chip: 'bg-slate-100 text-slate-700 border border-slate-200' };
}

/**
 * Human copy for a ReviewRequired trigger reason produced by auto-classification, or null when the
 * reason isn't classification-related (the caller then renders the raw reason unchanged).
 *   `classification_confidence:medium|low` → "En revisión por confianza de clasificación media/baja".
 */
export function describeClassificationReason(reason: string | null | undefined): string | null {
  if (reason == null || reason === '') {
    return null;
  }
  const prefix = 'classification_confidence:';
  if (reason.startsWith(prefix)) {
    const band = reason.slice(prefix.length).trim();
    return `En revisión por confianza de clasificación ${bandStyle(band).label}.`;
  }
  return null;
}
